/**
 * GreenToggle — controlled on/off toggle that's green when on.
 *
 * Used wherever the active state should read as "yes, this is live"
 * (SMS opt-in, an alert rule's enabled flag, etc.). Built as a plain
 * button so the GMB MUI theme can't override the thumb color the way
 * it does on `<Switch />`.
 *
 * Props:
 *   checked      — bool, current state
 *   onChange     — () => void, fired on click; receives no args (the
 *                  caller flips its own boolean)
 *   disabled     — bool, optional
 *   ariaLabel    — string, optional accessibility label
 *   size         — 'sm' | 'md', optional (defaults to 'md')
 */
import React from "react";
import SoftBox from "../SoftBox";

const SIZE = {
  sm: { w: 34, h: 18, thumb: 14, travel: 16 },
  md: { w: 40, h: 22, thumb: 18, travel: 20 },
};

const ON_BG = "#119031";
const OFF_BG = "#D0D5DD";

export default function GreenToggle({
  checked,
  onChange,
  disabled = false,
  ariaLabel,
  size = "md",
}) {
  const s = SIZE[size] || SIZE.md;
  return (
    <SoftBox
      component="button"
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={() => {
        if (disabled) return;
        onChange?.();
      }}
      sx={{
        width: `${s.w}px`,
        height: `${s.h}px`,
        borderRadius: "999px",
        border: 0,
        padding: 0,
        cursor: disabled ? "not-allowed" : "pointer",
        position: "relative",
        background: checked ? ON_BG : OFF_BG,
        opacity: disabled ? 0.55 : 1,
        transition: "background 160ms ease",
        flexShrink: 0,
        "&::after": {
          content: '""',
          position: "absolute",
          top: "2px",
          left: checked ? `${s.travel}px` : "2px",
          width: `${s.thumb}px`,
          height: `${s.thumb}px`,
          borderRadius: "50%",
          background: "#FFFFFF",
          boxShadow: "0 1px 3px rgba(0,0,0,0.20)",
          transition: "left 160ms ease",
        },
      }}
    />
  );
}
